import type { UseMutationOptions } from "@tanstack/react-query";

import { useMutationRequest, useRequest } from "~/v2/api/hooks";
import type { QueryOptions } from "~/v2/api/hooks/useRequest";
import {
  type ClosePositionsParams,
  closePositions,
  type GetPositionsParams,
  getPositions,
  positionsQueryKeys,
} from "~/v3-poc/api/positions";
import type { Position } from "~/v3-poc/api/positions.schema";
import { useInvalidate } from "~/v3-poc/kernel/react/Invalidation.context";

export type UseGetPositionsArgs = Omit<GetPositionsParams, "accountID"> & {
  accountID?: string;
  options?: QueryOptions<Position[]>;
};

export const useGetPositions = ({
  accountID,
  product,
  assetClass,
  maxDTEDays,
  side,
  symbols,
  options = {},
}: UseGetPositionsArgs) => {
  const params: GetPositionsParams = {
    accountID: accountID ?? "",
    product,
    assetClass,
    maxDTEDays,
    side,
    symbols,
  };
  const { data, ...result } = useRequest(
    positionsQueryKeys.list(params),
    () => getPositions(params),
    {
      ...options,
      meta: { showErrorToast: false },
    },
  );

  return { positions: data ?? [], ...result };
};

export const useClosePositions = (
  options?: Omit<
    UseMutationOptions<void, Error, ClosePositionsParams, unknown>,
    "mutationFn"
  >,
) => {
  const invalidate = useInvalidate();

  return useMutationRequest<void, ClosePositionsParams>(closePositions, {
    ...options,
    onSuccess: (...args) => {
      invalidate({ queryKey: positionsQueryKeys.all });
      options?.onSuccess?.(...args);
    },
  });
};
